/**
 * Module 'watch' (ADR 0058): the hub runs detectors on a short tick and raises incidents: what
 * is wrong, why it matters, who is responsible, the evidence, and the one action that clears it.
 *
 * An incident has a lifecycle (open, acknowledged, fixing, resolved, dismissed). Only its detector
 * resolves it, after the detector stopped firing for WATCH_RESOLVE_TICKS ticks in a row; people and
 * coding sessions acknowledge it, say they are fixing it (`orgus watch fixed`), or dismiss it with a
 * reason. One incident per detector and key: a repeat raises its count, never a second row.
 * With Autonomy Full, Orgus handles the ones it has a remedy for (ORGU-92: `handling`); Needs you
 * counts only those that need a person (incidentNeedsPerson).
 *
 *   GET  /api/watch                    WatchState
 *   POST /api/watch/:id/ack            { note? }
 *   POST /api/watch/:id/fixing         { note? }
 *   POST /api/watch/:id/fixed          { sha?, note? }: the fix is in; the detector proves it resolved
 *   POST /api/watch/:id/dismiss        { reason }
 *
 * On the hub's own computer the local session credential (LOCAL_WATCH_SCOPE, ADR 0058 4b) reaches
 * the list, ack, fixing and fixed, and nothing else; ack, fixing and fixed take `{ session?, branch? }`.
 */
import type { Id, Ts } from '../index.ts';
import { trimEndOf } from './trim.ts';

export type IncidentSeverity = 'critical' | 'high' | 'medium' | 'low';
export const INCIDENT_SEVERITIES: readonly IncidentSeverity[] = ['critical', 'high', 'medium', 'low'];
export const INCIDENT_SEVERITY_LABEL: Record<IncidentSeverity, string> = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };

export type IncidentState = 'open' | 'acknowledged' | 'fixing' | 'resolved' | 'dismissed';
export const INCIDENT_STATES: readonly IncidentState[] = ['open', 'acknowledged', 'fixing', 'resolved', 'dismissed'];
export const INCIDENT_STATE_LABEL: Record<IncidentState, string> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  fixing: 'Fixing',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

/**
 * The detectors (ADR 0058 decision 2, and the spend detectors of ADR 0062 decision 3). 'watch' is
 * the watch watching itself (a check that threw).
 */
export type IncidentDetector =
  | 'refusal-storm'
  | 'count-mismatch'
  | 'hub-drift'
  | 'landing-failed'
  | 'main-red'
  | 'flaky-test'
  | 'orphaned-work'
  | 'forgotten-process'
  | 'dev-server'
  | 'cleanup'
  | 'disk-low'
  | 'worktrees-heavy'
  | 'backup-stale'
  | 'stuck-session'
  | 'stale-failure'
  | 'cold-resumes'
  | 'context-heavy'
  | 'burn-without-progress'
  | 'failure-loop'
  | 'mission-environment'
  | 'loop-stale'
  | 'start-storm'
  | 'watch';

/**
 * How long a landing's proof that main is red (its failing tests failed on main itself, ADR 0088)
 * stands without running those tests on main again. Past it, while main has not moved, the next
 * landing reruns them before it refuses, and Orgus Watch shows "Rechecking main" instead of a
 * critical red: a cause that only lasted a while (machine load, a disk scan, an environment fixed
 * outside git) must not keep every landing refused until a person forces one.
 */
export const RED_PROOF_RECHECK_MS = 10 * 60_000;

export const INCIDENT_DETECTOR_LABEL: Record<IncidentDetector, string> = {
  'refusal-storm': 'Refusal storm',
  'count-mismatch': 'Count mismatch',
  'hub-drift': 'Hub drift',
  'landing-failed': 'Landing failed',
  'main-red': 'Main red',
  'flaky-test': 'Flaky test',
  'orphaned-work': 'Orphaned work',
  'forgotten-process': 'Forgotten process',
  'dev-server': 'Dev server',
  cleanup: 'Cleanup',
  'disk-low': 'Disk low',
  'worktrees-heavy': 'Worktrees heavy',
  'backup-stale': 'Backup stale',
  'stuck-session': 'Stuck session',
  'stale-failure': 'Stale failure',
  'cold-resumes': 'Cold resumes',
  'context-heavy': 'Context heavy',
  'burn-without-progress': 'Burn without progress',
  'failure-loop': 'Loop',
  'mission-environment': 'Mission environment',
  'loop-stale': 'Loop stale',
  'start-storm': 'Start storm',
  watch: 'Watch',
};

/**
 * The one action that clears an incident:
 * - command: run `target` (a shell command) where the incident says;
 * - open: open `target`, a console path ('/sessions?id=...');
 * - hand-back: pass every waiting request on machine `target` back to its session (ADR 0056);
 * - stop-server: stop the dev server `target` (`<machineId>/<pid>/<startToken>`, module
 *   'dev-servers') whose owner session is gone; the hub and the agent refuse it otherwise;
 * - hub: the hub runs `hub` itself (ORGU-186: POST /api/watch/:id/act, operator role, audited as
 *   "Orgus on behalf of <person>"), so a person acts from the phone; `command` is the same thing
 *   as a shell command, for a person at the machine (shown behind a disclosure);
 * - ack: nothing to run; a person or session takes it (acknowledge).
 */
export type IncidentActionKind = 'command' | 'open' | 'hand-back' | 'stop-server' | 'hub' | 'ack';

/**
 * What the hub runs for an incident (ORGU-186), each on what the watch last read, never on a
 * path or pid from the request:
 * - restart-follow-main: start `orgus follow-main` from the main checkout (hub drift);
 * - land-again: `orgus land --submit --done` from the failed branch's worktree;
 * - remove-worktree: keep any uncommitted leftovers under refs/salvage/<branch>, then remove a landed worktree and its branch;
 * - rearm-remedy: forget Orgus's failed tries, so the remedy runs again on the next cycle;
 * - stop-process: stop a dev server Orgus found in one of its worktrees (forgotten process).
 */
export type HubActionKind = 'restart-follow-main' | 'land-again' | 'remove-worktree' | 'rearm-remedy' | 'stop-process';
export const HUB_ACTION_KINDS: readonly HubActionKind[] = ['restart-follow-main', 'land-again', 'remove-worktree', 'rearm-remedy', 'stop-process'];

export interface IncidentAction {
  /** Verb plus object: "Pass waiting requests back", "Land the branch again". */
  label: string;
  kind: IncidentActionKind;
  target?: string;
  /** kind 'hub': what the hub runs. */
  hub?: HubActionKind;
  /** kind 'hub': the same action as a shell command, for a person at the machine. */
  command?: string;
}

/** Who a hub action is audited as: Orgus, on behalf of the person who asked. */
export const onBehalfOf = (person: string): string => `Orgus on behalf of ${person}`;

/** The shell command an action amounts to, if any: `target` for a command, `command` for a hub action. */
export function actionCommand(a: Pick<IncidentAction, 'kind' | 'target' | 'command'>): string | null {
  if (a.kind === 'command') return a.target ?? null;
  if (a.kind === 'hub') return a.command ?? null;
  return null;
}

/** Body of POST /api/watch/:id/act's answer for a hub action. */
export interface WatchActResult {
  incident: Incident;
  hub?: HubActionKind;
  /** One plain sentence: what the hub did. */
  message?: string;
}

export type IncidentResponsibleKind = 'session' | 'branch' | 'machine' | 'person' | 'agent';

export interface IncidentResponsible {
  kind: IncidentResponsibleKind;
  id: string;
  label: string;
}

/** One fact of the evidence, ready to show: "Refusals in the last minute: 42". */
export interface IncidentFact {
  label: string;
  value: string | number;
}

/** What an incident concerns, so `orgus watch` lists it to the session, branch or machine it is about. */
export interface IncidentScope {
  machineId?: Id;
  sessionId?: Id;
  branch?: string;
  /** A worktree folder, absolute, as the detector saw it. */
  worktree?: string;
  /** True for incidents about main itself (main red, hub drift): every caller sees them. */
  main?: boolean;
  /** The project whose main it is (Overview shows each project's main apart). Absent: an older hub, or about the hub itself. */
  projectId?: Id;
}

export interface Incident {
  /** inc_<hash of the key>: stable for the key, so a repeat is the same incident. */
  id: Id;
  detector: IncidentDetector;
  /** Dedupe key within the detector: the machine, the branch, the session, the test. */
  key: string;
  severity: IncidentSeverity;
  state: IncidentState;
  /** One plain sentence, no em dash. */
  title: string;
  /** Why it matters, one or two sentences. */
  why: string;
  evidence: IncidentFact[];
  action: IncidentAction;
  responsible: IncidentResponsible;
  scope: IncidentScope;
  openedAt: Ts;
  updatedAt: Ts;
  /** The last tick the detector fired for it. */
  lastSeenAt: Ts;
  resolvedAt?: Ts;
  /** 'watch' when the detector stopped firing; who dismissed it otherwise. */
  resolvedBy?: string;
  /** Who acknowledged, said fixing or fixed, or dismissed it, and when. */
  by?: string;
  /** The fix's commit, from `orgus watch fixed --sha`. */
  sha?: string;
  /** ORGU-299: The revert commit linked to this incident. */
  revertSha?: string;
  /** ORGU-299: The fix session linked to this incident. */
  fixSession?: Id;
  /** A person's or session's note; the dismissal reason. */
  note?: string;
  /** How many times it opened (repeats); a detector may count occurrences instead (flaky tests). */
  count: number;
  /** Who handles it and how (ORGU-92, ADR 0064 amended); absent: a person, as before. */
  handling?: IncidentHandling;
  /** ORGU-135: the owner's answer to "Drop <branch>" (ask mode): approved drops it, denied leaves it in place. Additive, 30 Oct 2026. */
  dropDecision?: { answer: 'approved' | 'denied'; by: string; at: Ts };
}

/** ORGU-135: the drop verdict a land-or-drop mission recorded (`orgus watch fixing <id> --note "Drop: <why>"`), else null. */
export function dropVerdict(i: Pick<Incident, 'note'>): string | null {
  const m = /^\s*Drop:\s*([\s\S]*\S)/i.exec(i.note ?? '');
  return m ? m[1]! : null;
}

/** ORGU-135: where a dropped branch's tip and uncommitted files are kept. */
export function salvageRef(branch: string): string {
  return `refs/salvage/${branch.replace(/[^\w./-]+/g, '-').replace(/\.\.+/g, '.').replace(/^[./-]+|[./]+$/g, '') || 'branch'}`;
}

// ── Orgus handles it (ORGU-92, ADR 0064 amendment of 19 Oct 2026) ───────────────────────────

/**
 * How Orgus handles an incident:
 * - working: a remedy is under way (a mission, a removal, the landing queue's wake), or Orgus waits
 *   its turn (one mission per worktree, a few at a time);
 * - info: informational, it clears by itself (cold resumes, context heavy, forgotten processes);
 * - gave-up: Orgus tried and could not, or cannot act on it at all: a person decides;
 * - people: the owner chose People (or Autonomy is not Full), so a person handles it.
 */
export type IncidentHandlingState = 'working' | 'info' | 'gave-up' | 'people';

/** What Orgus does about it: remove a landed worktree, run a mission, let the landing queue wake its session, revert the commit that broke main (additive, 21 Oct 2026), or nothing. */
export type IncidentRemedy = 'remove-worktree' | 'mission' | 'wake' | 'revert' | 'submit' | 'salvage' | 'drop' | 'none';

/**
 * ORGU-138: how orphaned work is triaged by cost, cheapest first. merged: every commit on main,
 * removed. salvage: merged with uncommitted leftovers, kept as a ref and removed. submit: unique
 * commits that merge clean and pass the fast checks, landed through the queue without a mission.
 * mission: the rest, one land-or-drop mission each.
 */
export type OrphanTier = 'merged' | 'salvage' | 'submit' | 'mission';
export const ORPHAN_TIERS: readonly OrphanTier[] = ['merged', 'salvage', 'submit', 'mission'];

export interface IncidentHandling {
  state: IncidentHandlingState;
  remedy: IncidentRemedy;
  /** One plain sentence: "Orgus is handling this: a mission lands the branch or records why to drop it." */
  text: string;
  /** The mission doing it, when there is one. */
  missionId?: Id;
  /** ORGU-299: The fix session doing it, when there is one. */
  sessionId?: Id;
  /** ORGU-299: The revert commit linked to this incident. */
  revertSha?: string;
  /** Which try this is (1 based), for remedies that retry. */
  attempt?: number;
  /**
   * What the remedy waits on before it can start (ADR 0064 amended 21 Oct 2026): no agent Orgus
   * may use is on, or a policy or the machine's ceiling refused the start. Absent when nothing
   * blocks it. Additive, 21 Oct 2026.
   */
  waitingOn?: string;
  /** Orphaned work: its triage tier (ORGU-138). Additive, 30 Oct 2026. */
  tier?: OrphanTier;
  /** ORGU-135: the one approval a person answers ("Drop <branch>: <why>"), with the ref it is kept under. Additive, 30 Oct 2026. */
  approval?: { verb: 'drop'; title: string; ref: string };
  since: Ts;
}

/** Detectors that only inform: they clear by themselves and never count in Needs you. */
export const INFORMATIONAL_DETECTORS: readonly IncidentDetector[] = ['cold-resumes', 'context-heavy', 'forgotten-process', 'dev-server', 'cleanup', 'worktrees-heavy'];

export function incidentInformational(i: Pick<Incident, 'detector'>): boolean {
  return INFORMATIONAL_DETECTORS.includes(i.detector);
}

/**
 * Whether an active incident needs a person: not informational, and Orgus is not handling it
 * (it gave up, the owner chose People, or nothing handles that detector). The one rule Needs you
 * and the top bar count by.
 */
export function incidentNeedsPerson(i: Pick<Incident, 'state' | 'detector' | 'handling'>): boolean {
  if (!incidentActive(i) || incidentInformational(i)) return false;
  const h = i.handling?.state;
  return h !== 'working' && h !== 'info';
}

/** One detector's last run, so a check that throws or stops shows. */
export interface WatchDetectorRun {
  /** The check's id ('landings' raises landing-failed, main-red and flaky-test). */
  detector: string;
  lastRunAt: Ts | null;
  /** True when the last run had nothing to look at (no repo, not on this platform). */
  skipped?: boolean;
  /** Why the last run failed, or null. */
  error: string | null;
}

export interface WatchState {
  /** Every incident kept (WATCH_KEEP_MS), newest change first. */
  incidents: Incident[];
  /** When the last tick finished; null before the first. */
  lastTickAt: Ts | null;
  tickMs: number;
  detectors: WatchDetectorRun[];
}

/** Body of POST /api/watch/:id/{ack,fixing,fixed,dismiss}. */
export interface WatchUpdateBody {
  note?: string;
  sha?: string;
  /** dismiss only: required. */
  reason?: string;
  /** Who is reporting, as the caller says (audited, and shown as the incident's `by` for a local session). */
  session?: string;
  branch?: string;
}

/** The watch's audit actions as words (QA-37: the activity feed showed "watch.open"). */
export const WATCH_AUDIT_LABEL: Record<string, string> = {
  'watch.open': 'Watch found a problem',
  'watch.resolve': 'Watch problem resolved',
  'watch.ack': 'Watch problem taken',
  'watch.fixing': 'Watch fix under way',
  'watch.fixed': 'Watch problem fixed',
  'watch.dismiss': 'Watch problem dismissed',
  'watch.act': 'Watch action run',
  'watch.force_red': 'Landed onto a red main',
  'watch.remedy': 'Watch started a remedy',
  'watch.gave_up': 'Watch gave up a remedy',
};

export const WATCH_TICK_MS = 30_000;
/** A detector that stops firing this many ticks in a row resolves its incident. */
export const WATCH_RESOLVE_TICKS = 2;
/** Incidents are kept 30 days, so a repeat shows its history. */
export const WATCH_KEEP_MS = 30 * 24 * 60 * 60_000;
export const WATCH_KEEP_MAX = 500;
/** No tick for this many tick lengths: the watch itself is stale. */
export const WATCH_STALE_TICKS = 4;
export const WATCH_NOTE_MAX = 1000;

const SEVERITY_RANK: Record<IncidentSeverity, number> = { critical: 0, high: 1, medium: 2, low: 3 };

/** Open, acknowledged or fixing: still needs work. */
export function incidentActive(i: Pick<Incident, 'state'>): boolean {
  return i.state === 'open' || i.state === 'acknowledged' || i.state === 'fixing';
}

/** Severity first, then the oldest first. */
export function compareIncidents(a: Incident, b: Incident): number {
  return SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || a.openedAt - b.openedAt;
}

/** The Attention list: active incidents, ranked; ones nobody took yet before taken ones of the same severity. */
export function attentionOf(incidents: readonly Incident[]): Incident[] {
  const taken = (i: Incident) => (i.state === 'open' ? 0 : 1);
  return incidents.filter(incidentActive).sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || taken(a) - taken(b) || a.openedAt - b.openedAt);
}

/** What the top bar notice count adds: incidents nobody has taken yet that need a person. */
export function openIncidentCount(incidents: readonly Incident[]): number {
  return incidents.filter((i) => i.state === 'open' && incidentNeedsPerson(i)).length;
}

/** True when the watch has not ticked for WATCH_STALE_TICKS tick lengths (the watch watches itself). */
export function watchStale(state: Pick<WatchState, 'lastTickAt' | 'tickMs'> | null | undefined, now: Ts): boolean {
  if (!state || state.lastTickAt == null) return false;
  return now - state.lastTickAt > state.tickMs * WATCH_STALE_TICKS;
}

/** Who is asking `orgus watch`: its session, branch, worktree and machine, as far as it knows. */
export interface WatchCaller {
  sessionId?: string;
  branch?: string;
  worktree?: string;
  machineId?: string;
}

const norm = (p: string) => trimEndOf(p.replace(/\\/g, '/'), '/').toLowerCase();

/** Whether an incident concerns the caller: main is everyone's; otherwise its session, branch, worktree or machine. */
export function incidentConcerns(i: Pick<Incident, 'scope' | 'responsible'>, c: WatchCaller): boolean {
  const s = i.scope;
  if (s.main) return true;
  if (c.sessionId && s.sessionId === c.sessionId) return true;
  if (c.branch && s.branch === c.branch) return true;
  if (c.worktree && s.worktree) {
    const a = norm(s.worktree);
    const b = norm(c.worktree);
    if (a === b || b.startsWith(`${a}/`)) return true;
  }
  if (c.machineId && s.machineId === c.machineId && !s.sessionId && !s.branch && !s.worktree) return true;
  return false;
}

/** A stable incident id for a detector and key (FNV-1a, browser-safe). */
export function incidentId(detector: IncidentDetector, key: string): Id {
  let h = 0x811c9dc5;
  const s = `${detector}\u0000${key}`;
  for (let k = 0; k < s.length; k++) {
    h ^= s.charCodeAt(k);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  let h2 = 0x01000193;
  for (let k = s.length - 1; k >= 0; k--) {
    h2 ^= s.charCodeAt(k);
    h2 = Math.imul(h2, 0x811c9dc5) >>> 0;
  }
  return `inc_${h.toString(36)}${h2.toString(36)}`;
}

// ── The local session credential (ADR 0058 4b, amended 18 Oct 2026) ─────────────────────────

/**
 * The only scope of the local session credential. A token carrying it reaches exactly the
 * routes localWatchRoute names, whatever else it carries; nothing else on the hub (no other
 * read, no dismiss, no action, no AI capability). Only the hub mints it: Settings never
 * accepts it on a token.
 */
export const LOCAL_WATCH_SCOPE = 'local-watch';
/** The agent token name of the local session credential: it acts, and is audited, as agent:local-session. */
export const LOCAL_SESSION_TOKEN_NAME = 'local-session';
export const LOCAL_SESSION_ACTOR = `agent:${LOCAL_SESSION_TOKEN_NAME}`;
/** Where a hub keeps it: <home>/cli/local-sessions/port-<port>.json, owner-only. */
export const LOCAL_SESSION_DIR = ['cli', 'local-sessions'] as const;
export const localSessionFileName = (port: number): string => `port-${port}.json`;
/** Variables the agent hands to the sessions it launches, so they never read the Orgus home. */
export const WATCH_URL_ENV = 'ORGUS_WATCH_URL';
export const WATCH_TOKEN_ENV = 'ORGUS_WATCH_TOKEN';

/** What the hub writes for the local session credential. Holds the secret; never logged or printed. */
export interface LocalSessionCredential {
  v: 1;
  /** The hub, on this computer (loopback). */
  url: string;
  port: number;
  token: string;
  tokenId: string;
  expiresAt: Ts;
  writtenAt: Ts;
  /** The credential before the last rotation: it keeps working until it expires, so running sessions are not cut off. */
  previous?: { tokenId: string; expiresAt: Ts };
}

/**
 * The landing queue's local credential (ADR 0055, amended 19 Oct 2026): the hub mints it beside
 * the local session credential, for the lander to wake a session whose branch was rejected and to
 * tell one its branch landed. It reaches only localLanderRoute's routes, only from this computer,
 * and is never handed to sessions (unlike the local session credential, which every session Orgus
 * launches carries): a session cannot use it to write into another session.
 */
export const LOCAL_LANDER_SCOPE = 'local-lander';
export const LOCAL_LANDER_TOKEN_NAME = 'local-lander';
export const localLanderFileName = (port: number): string => `lander-port-${port}.json`;

/** The routes the lander credential reaches: wake a session, tell it its branch landed. */
export function localLanderRoute(method: string, pathname: string): boolean {
  return method.toUpperCase() === 'POST' && /^\/api\/sessions\/[^/]+\/(wake|landed|admit)$/.test(pathname);
}

/** The routes a local session credential reaches: read incidents; acknowledge, fixing, fixed. */
export function localWatchRoute(method: string, pathname: string): boolean {
  const m = method.toUpperCase();
  if (m === 'GET') return pathname === '/api/watch';
  return m === 'POST' && /^\/api\/watch\/[^/]+\/(ack|fixing|fixed)$/.test(pathname);
}

/** True for an http:// URL on this computer (127.x, ::1, localhost). */
export function isLoopbackUrl(url: string): boolean {
  return /^http:\/\/(localhost|\[::1\]|127(\.\d{1,3}){3})(:\d{1,5})?(\/[^\s]*)?$/i.test(url.trim());
}

/** The port of a loopback URL (80 when it names none), or null. */
export function loopbackPort(url: string): number | null {
  if (!isLoopbackUrl(url)) return null;
  const m = /^http:\/\/(?:localhost|\[::1\]|[0-9.]+)(?::(\d{1,5}))?/i.exec(url.trim());
  const port = m?.[1] ? Number(m[1]) : 80;
  return port >= 1 && port <= 65535 ? port : null;
}

/** A credential file as read, checked: for this port, not expired, well formed; else null. */
export function parseLocalSessionCredential(raw: unknown, port: number, now: Ts): LocalSessionCredential | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const c = raw as Partial<LocalSessionCredential>;
  if (c.v !== 1 || c.port !== port || typeof c.url !== 'string' || !isLoopbackUrl(c.url)) return null;
  if (typeof c.token !== 'string' || !c.token || typeof c.tokenId !== 'string') return null;
  if (typeof c.expiresAt !== 'number' || c.expiresAt <= now) return null;
  return c as LocalSessionCredential;
}

// ── Main's health from the organization's CI (ADR 0077 decision 2) ─────────────────────────

/**
 * Whether CI covers main, written by the hub's watch to <hub dir>/watch/ci-main.json on every
 * tick (<hub dir> is <ORGUS_HOME>/hub) for the repo the hub runs from, and read by `orgus
 * follow-main`, which starts that hub with that ORGUS_HOME. Covered: an active CI connection for
 * the repo's project finished a run on its default branch within CI_MAIN_RECENT_MS. Then the
 * local full suite on main (main-check, ADR 0068) does not run: CI's runs on the default branch
 * are main's health. A file older than CI_MAIN_FRESH_MS (the hub stopped) does not cover, so
 * the local check comes back on its own when CI or the hub goes away.
 */
export interface CiMainCoverage {
  v: 1;
  /** The main checkout of the repo the hub runs from. */
  repo: string;
  projectId: Id | null;
  /** The project's default branch. */
  branch: string;
  covered: boolean;
  /** The CI connections that cover it (titles), for the log line. */
  by: string[];
  /** The newest finished run on the default branch. */
  lastRunAt: Ts | null;
  at: Ts;
}

export const CI_MAIN_FILE = 'ci-main.json';
/** A finished run on the default branch this recent means CI runs on main. */
export const CI_MAIN_RECENT_MS = 7 * 24 * 60 * 60_000;
/** The hub rewrites the file every tick; older than this, the hub is gone and it does not count. */
export const CI_MAIN_FRESH_MS = 10 * 60_000;

/** ci-main.json as read: the coverage when it covers `repo` now; null when missing, stale, for another repo or not covered. */
export function ciMainCoverage(raw: string | null, repo: string, now: Ts): CiMainCoverage | null {
  if (!raw) return null;
  let j: Partial<CiMainCoverage> | null;
  try {
    j = JSON.parse(raw) as Partial<CiMainCoverage> | null;
  } catch {
    return null;
  }
  if (!j || typeof j !== 'object' || j.v !== 1 || j.covered !== true || typeof j.repo !== 'string' || typeof j.at !== 'number') return null;
  if (now - j.at > CI_MAIN_FRESH_MS || norm(j.repo) !== norm(repo)) return null;
  return {
    v: 1,
    repo: j.repo,
    projectId: typeof j.projectId === 'string' ? j.projectId : null,
    branch: typeof j.branch === 'string' ? j.branch : 'main',
    covered: true,
    by: Array.isArray(j.by) ? j.by.filter((b): b is string => typeof b === 'string').slice(0, 5) : [],
    lastRunAt: typeof j.lastRunAt === 'number' ? j.lastRunAt : null,
    at: j.at,
  };
}
